import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheckCircle, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const OVERVIEW_SLIDER_IMAGES = [
  { src: "https://assocham.org/assocham_backend/assets/images/environmental-sustainability.png", alt: "Environmental Sustainability" },
  { src: "https://assocham.org/assocham_backend/assets/images/diversity-inclusion.png", alt: "Diversity & Inclusion" },
  { src: "https://assocham.org/assocham_backend/assets/images/albums/1755027630_3.jpg", alt: "On-Ground Impact" },
];

const AUTOPLAY_MS = 3800;

function OverviewSlider() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback(
    (next) => {
      setDirection(
        next > index || (index === OVERVIEW_SLIDER_IMAGES.length - 1 && next === 0) ? 1 : -1
      );
      setIndex(next);
    },
    [index]
  );

  const next = useCallback(
    () => goTo((index + 1) % OVERVIEW_SLIDER_IMAGES.length),
    [index, goTo]
  );
  const prev = useCallback(
    () => goTo((index - 1 + OVERVIEW_SLIDER_IMAGES.length) % OVERVIEW_SLIDER_IMAGES.length),
    [index, goTo]
  );

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [next, isPaused]);

  const variants = {
    enter: (dir) => ({ opacity: 0, scale: 1.12, x: dir > 0 ? 50 : -50 }),
    center: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] },
    },
    exit: (dir) => ({
      opacity: 0,
      scale: 1.05,
      x: dir > 0 ? -50 : 50,
      transition: { duration: 0.65, ease: "easeInOut" },
    }),
  };

  return (
    <motion.div
      initial={{ x: 28, opacity: 0 }}
      whileInView={{ x: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, delay: 0.1 }}
      className="relative"
    >
      <div
        className="group relative overflow-hidden rounded-[32px] shadow-2xl shadow-[#DA2128]/10"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative h-96 w-full lg:h-[27rem]">
          <AnimatePresence custom={direction} initial={false} mode="popLayout">
            <motion.div
              key={index}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0"
            >
              <motion.img
                src={OVERVIEW_SLIDER_IMAGES[index].src}
                alt={OVERVIEW_SLIDER_IMAGES[index].alt}
                className="h-full w-full object-cover"
                initial={{ scale: 1.18 }}
                animate={{ scale: 1 }}
                transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
              />
            </motion.div>
          </AnimatePresence>

          <div className="absolute inset-0 bg-gradient-to-t from-[#12100E]/75 via-[#12100E]/10 to-transparent" />
          <span className="sheen-layer" />

          {/* Arrows */}
          <button
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur-md transition-all duration-300 hover:bg-[#DA2128] group-hover:opacity-100"
          >
            <FaChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur-md transition-all duration-300 hover:bg-[#DA2128] group-hover:opacity-100"
          >
            <FaChevronRight className="h-3.5 w-3.5" />
          </button>

          {/* Progress bar */}
          <div className="absolute left-0 right-0 top-0 z-20 h-[3px] bg-white/20">
            {!isPaused && (
              <motion.div
                key={index}
                className="h-full bg-[#DA2128]"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
              />
            )}
          </div>

          {/* Dots */}
          <div className="absolute bottom-24 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {OVERVIEW_SLIDER_IMAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === index ? "w-6 bg-[#DA2128]" : "w-1.5 bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>

        {/* <div className="glass absolute inset-x-5 bottom-5 z-20 rounded-[32px] p-5">
          <div className="font-display text-3xl text-white">05</div>
          <div className="text-xs text-white/75">
            Thematic pillars aligned with national development priorities
          </div>
        </div> */}
      </div>

      <div className="absolute -left-5 -top-5 hidden rounded-[28px] border border-[#EFE9DF] bg-white p-4 shadow-xl md:block">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#6E6058]">Since</div>
        <div className="font-display text-3xl leading-none text-[#DA2128]">2013</div>
      </div>
      <div className="absolute -bottom-6 -right-4 hidden w-44 rounded-[28px] border border-[#EFE9DF] bg-white p-4 shadow-xl lg:block">
        <div className="flex items-center gap-2 text-[11px] font-bold text-[#1A1714]">
          <FaCheckCircle className="h-3.5 w-3.5 text-[#2F7D4F]" /> Verified impact
        </div>
        <p className="mt-1 text-[10.5px] leading-snug text-[#6E6058]">
          Third-party audited programme reporting across 22 states.
        </p>
      </div>
    </motion.div>
  );
}

function SectionTitle({ eyebrow, title, italic, align = "center", sub }) {
  return (
    <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <span className="inline-block text-[10px] font-bold uppercase tracking-[0.24em] text-[#DB6400]">
        {eyebrow}
      </span>
      <h2 className="font-display mt-3 text-3xl font-medium leading-[1.05] tracking-tight text-[#1A1714] md:text-4xl lg:text-[2.9rem]">
        {title} {italic && <span className="text-gradient-ember italic">{italic}</span>}
      </h2>
      {sub && <p className="mt-4 text-[13px] leading-relaxed text-[#1A1714]/60">{sub}</p>}
      <div
        className={`mt-5 h-[3px] w-16 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] ${
          align === "center" ? "mx-auto" : ""
        }`}
      />
    </div>
  );
}

function AFCSROverview() {
  return (
    <section id="overview" className="relative overflow-hidden bg-[#FAF7F2] py-20 lg:py-28">
      <div className="animate-glow absolute -left-32 top-20 h-96 w-96 rounded-full bg-[#DA2128]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <motion.div
            initial={{ x: -28, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <SectionTitle
              eyebrow="Foundation Overview"
              title="Rooted in the legacy of"
              italic="ASSOCHAM"
              align="left"
            />
            <div className="mt-7 space-y-4 text-[13px] leading-relaxed text-[#1A1714]/70">
              <p>
                Rooted in the legacy of <strong className="text-[#1A1714]">ASSOCHAM</strong>, India's Apex Chamber of Commerce, the ASSOCHAM Foundation for Corporate Social Responsibility (AFCSR) was established as a Trust in <strong className="text-[#DA2128]">2013</strong> to bridge the gap between public policy and grassroots action, delivering impactful programmes in partnership with corporates, government bodies, development organisations, and communities.
              </p>
              <p>
                AFCSR is dedicated to identifying critical social, developmental, and environmental challenges that require urgent attention, measurable outcomes, and sustainable solutions, contributing to the vision of <strong className="text-[#1A1714]">Viksit Bharat 2047</strong>.
              </p>
              <p>
                As the social arm of ASSOCHAM, AFCSR plays a pivotal role in advancing inclusive and sustainable development through well-designed programmes and strategic partnerships. With a focus on education, healthcare, skilling, livelihoods, environmental sustainability, climate action, and community empowerment, the Foundation promotes responsible business practices, ESG-led initiatives, and industry–government collaboration to create long-term social impact and support India's development journey.
              </p>
            </div>
            {/* <div className="mt-7 flex flex-wrap gap-2">
              {[
                "Education",
                "Healthcare",
                "Skilling",
                "Livelihoods",
                "Environmental Sustainability",
                "Climate Action",
                "Community Empowerment",
              ].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-[#EFE9DF] bg-[#FEF7F4] px-3.5 py-1.5 text-[11px] font-semibold text-[#DB6400]"
                >
                  {tag}
                </span>
              ))}
            </div> */}
          </motion.div>

          <OverviewSlider />
        </div>
      </div>
    </section>
  );
}

export default AFCSROverview;